/**
 * Error handling utilities
 */

const isPlainObject = (value) => {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
};

/**
 * Extract Django REST Framework field validation errors from an Axios error.
 * Returns a mapping of fieldName -> [messages].
 *
 * Example DRF payload:
 *  { department: ["This field is required."] }
 */
export const extractFieldErrors = (error) => {
  const data = error?.response?.data;
  if (!isPlainObject(data)) return {};

  const ignoredKeys = new Set(['detail', 'error', 'message']);
  const fieldErrors = {};

  for (const [field, value] of Object.entries(data)) {
    if (ignoredKeys.has(field)) continue;

    if (Array.isArray(value)) {
      const messages = value.filter(Boolean).map((m) => String(m));
      if (messages.length > 0) fieldErrors[field] = messages;
      continue;
    }

    if (typeof value === 'string') {
      const msg = value.trim();
      if (msg) fieldErrors[field] = [msg];
      continue;
    }
  }

  return fieldErrors;
};

export const formatFieldErrors = (fieldErrors) => {
  if (!fieldErrors || typeof fieldErrors !== 'object') return '';
  const parts = Object.entries(fieldErrors)
    .filter(([_, messages]) => Array.isArray(messages) && messages.length > 0)
    .map(([field, messages]) => `${field}: ${messages.join(', ')}`);
  return parts.join('; ');
};

/**
 * Extract error message from various error formats
 * @param {Error|Object} error - Error object
 * @param {string} defaultMessage - Default message if error cannot be parsed
 * @returns {string} Human-readable error message
 */
export const getErrorMessage = (error, defaultMessage = 'An error occurred') => {
  if (!error) return defaultMessage;

  // Axios error response
  if (error.response?.data) {
    const data = error.response.data;
    
    // Django REST Framework error format
    if (data.detail) return data.detail;
    if (data.error) return data.error;
    if (data.message) return data.message;

    // DRF sometimes returns a list of strings for non-field errors
    if (Array.isArray(data)) {
      const msg = data.filter(Boolean).map(String).join('; ');
      if (msg) return msg;
    }
    
    // Field errors
    if (isPlainObject(data)) {
      const fieldErrors = extractFieldErrors(error);
      const formatted = formatFieldErrors(fieldErrors);
      if (formatted) return formatted;
    }
  }

  // Standard Error object
  if (error.message) return error.message;

  // String error
  if (typeof error === 'string') return error;

  // Fallback
  return defaultMessage;
};

/**
 * Parse API error into structured form for dispatch/validation UIs.
 * @param {Error|Object} error - Axios or similar error
 * @param {string} defaultMessage - Fallback message
 * @returns {{ message: string, errorCode?: string, context?: Object, fieldErrors: Object }}
 */
export const parseStructuredError = (error, defaultMessage = 'An error occurred') => {
  const fieldErrors = extractFieldErrors(error || {});
  let message = getErrorMessage(error, defaultMessage);
  const data = error?.response?.data;
  const errorCode = isPlainObject(data) && typeof data.error_code === 'string' ? data.error_code : undefined;
  const context = isPlainObject(data) && isPlainObject(data.context) ? data.context : undefined;
  return { message, errorCode, context, fieldErrors };
};

/**
 * Build operator-friendly message for dispatch validation errors (e.g. INSUFFICIENT_PIECES, INSUFFICIENT_WEIGHT).
 * @param {{ message: string, errorCode?: string, context?: Object }} parsed - From parseStructuredError
 * @returns {string} Human-readable message
 */
export const formatDispatchValidationMessage = (parsed) => {
  const { message, errorCode, context } = parsed;
  const packingId = context?.packing_id || '';
  const available = context?.available;
  const requested = context?.requested;

  if (errorCode === 'INSUFFICIENT_PIECES' && packingId && available != null && requested != null) {
    return `Packing ${packingId}: only ${available} pcs remaining (you entered ${requested}). Reduce pieces or refresh stock.`;
  }
  if (errorCode === 'INSUFFICIENT_WEIGHT' && packingId && available != null && requested != null) {
    return `Packing ${packingId}: only ${available} kg remaining (you entered ${requested}). Reduce weight or refresh stock.`;
  }
  if (errorCode === 'WORK_ORDER_MISMATCH' && packingId) {
    const expectedWo = context?.expected_wo || 'N/A';
    const actualWo = context?.actual_wo || 'N/A';
    return `Work Order Mismatch for Packing ${packingId}: belongs to work order "${actualWo}", but the consignment expects "${expectedWo}". Please select a matching consignment or change the consignment's work order to match.`;
  }
  if (errorCode === 'CLIENT_MISMATCH' && packingId) {
    return `Client Mismatch for Packing ${packingId}: the packing belongs to a different client than the consignment it is allocated to. Please allocate it to the correct consignment.`;
  }
  if (errorCode === 'MISSING_BATCH_ORDER' && packingId) {
    return `Invalid Packing ${packingId}: this packing is missing its corresponding production batch or order details.`;
  }
  if (errorCode === 'NO_PACKING_IN_ROUTE' && packingId) {
    return `Routing Error for Packing ${packingId}: the production batch route for this item does not include a packing step.`;
  }
  if (errorCode === 'BATCH_STATE_INVALID' && packingId) {
    const state = context?.state || 'N/A';
    return `Invalid Batch State for Packing ${packingId}: the batch is currently in "${state}" state (must be PACKAGED or IN_STOCK).`;
  }
  if (errorCode === 'EVIDENCE_INCOMPLETE' && packingId) {
    return `Evidence Incomplete for Packing ${packingId}: the packing unit evidence is not completed yet.`;
  }
  if (errorCode === 'INVALID_PACKING_STATUS' && packingId) {
    const status = context?.status || 'N/A';
    return `Invalid Status for Packing ${packingId}: status is "${status}" (must be "packed" or "partially_dispatched").`;
  }
  if (errorCode === 'NO_REMAINING_PIECES' && packingId) {
    return `Out of Stock for Packing ${packingId}: this packing has no remaining pieces to dispatch.`;
  }
  if (errorCode === 'INVALID_WEIGHT' && packingId) {
    return `Invalid Weight for Packing ${packingId}: this packing has invalid weight parameters.`;
  }

  return message;
};

/**
 * Check if error is a network error
 * @param {Error|Object} error - Error object
 * @returns {boolean} True if network error
 */
export const isNetworkError = (error) => {
  if (!error) return false;
  
  // No response means network error
  if (!error.response) return true;
  
  // Timeout errors
  if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
    return true;
  }
  
  return false;
};

/**
 * Check if error is an authentication error
 * @param {Error|Object} error - Error object
 * @returns {boolean} True if authentication error
 */
export const isAuthError = (error) => {
  if (!error) return false;
  return error.response?.status === 401 || error.response?.status === 403;
};

/**
 * Check if error is a validation error
 * @param {Error|Object} error - Error object
 * @returns {boolean} True if validation error
 */
export const isValidationError = (error) => {
  if (!error) return false;
  return error.response?.status === 400;
};

/**
 * Handle API error with toast notification
 * @param {Error|Object} error - Error object
 * @param {Function} toast - Toast function from useToast hook
 * @param {string} defaultMessage - Default error message
 * @param {Object} options - Additional options
 * @param {boolean} options.logError - Whether to log error to console (default: true)
 */
export const handleApiError = (error, toast, defaultMessage = 'An error occurred', options = {}) => {
  const { logError = true } = options;
  
  if (logError) {
    console.error('API Error:', error);
  }
  
  const message = getErrorMessage(error, defaultMessage);
  
  if (toast && typeof toast.error === 'function') {
    toast.error(message);
  }
  
  return message;
};

/**
 * Create a safe async handler that catches and handles errors
 * @param {Function} asyncFn - Async function to wrap
 * @param {Function} onError - Error handler function
 * @returns {Function} Wrapped function
 */
export const createSafeAsyncHandler = (asyncFn, onError) => {
  return async (...args) => {
    try {
      return await asyncFn(...args);
    } catch (error) {
      if (onError) {
        onError(error);
      } else {
        console.error('Unhandled error in async handler:', error);
      }
      throw error;
    }
  };
};
